import { setMathMLDisplayAttribute } from '@/lib/utils';
import { InputRule, mergeAttributes, Node } from '@tiptap/core';
import { type KatexOptions } from 'katex';
import { createMathematicsNodeView } from '../mathematics-node-view';

/**
 * Configuration options for the InlineMath extension.
 */
export type InlineMathOptions = {
    /**
     * Renderer to use for inline math. Default to 'mathjax' if not specified.
     * Set to 'katex' to use KaTeX instead.
     */
    renderer?: 'mathjax' | 'katex';

    /**
     * KaTeX specific options
     * @see https://katex.org/docs/options.html
     * @example
     * ```ts
     * katexOptions: {
     *   displayMode: false,
     *   throwOnError: false,
     *   macros: {
     *     '\\RR': '\\mathbb{R}',
     *     '\\ZZ': '\\mathbb{Z}'
     *   }
     * }
     * ```
     */
    katexOptions?: KatexOptions;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        inlineMath: {
            /**
             * Insert a inline math node with LaTeX string.
             * @param options - Options for inserting inline math.
             * @returns ReturnType
             */
            insertInlineMath: (options: { latex?: string; mathml?: string; pos?: number }) => ReturnType;

            /**
             * Delete an inline math node.
             * @returns ReturnType
             */
            deleteInlineMath: (options?: { pos?: number }) => ReturnType;

            /**
             * Update inline math node with optional LaTeX string.
             * @param options - Options for updating inline math.
             * @returns ReturnType
             */
            updateInlineMath: (options?: { latex?: string; mathml?: string; pos?: number }) => ReturnType;
        };
    }
}

/**
 * InlineMath is a Tiptap extension for rendering inline mathematical expressions using KaTeX.
 * It allows users to insert LaTeX formatted math expressions inline within text.
 * It supports rendering, input rules for LaTeX syntax, and click handling for interaction.
 *
 * @example
 * ```javascript
 * import { InlineMath } from '@tiptap/extension-mathematics'
 * import { Editor } from '@tiptap/core'
 *
 * const editor = new Editor({
 *   extensions: [InlineMath],
 * })
 */
export const InlineMathHeadless = Node.create<InlineMathOptions>({
    name: 'inlineMath',

    group: 'inline',

    inline: true,

    atom: true,

    selectable: true,

    // Allows CommentsKit inlineThread (and other marks) to attach when commenting on the node alone.
    marks: '_',

    addOptions() {
        return {
            katexOptions: undefined,
        };
    },

    addAttributes() {
        return {
            latex: {
                default: '',
                parseHTML: element => element.getAttribute('data-latex'),
                renderHTML: attributes => {
                    return {
                        'data-latex': attributes.latex,
                    };
                },
            },
            mathml: {
                default: '',
                parseHTML: element =>
                    element.tagName?.toLowerCase() === 'math'
                        ? setMathMLDisplayAttribute((element as HTMLElement).outerHTML, false)
                        : setMathMLDisplayAttribute(element.getAttribute('data-mathml') || '', false),
                renderHTML: attributes => {
                    return {
                        'data-mathml': attributes.mathml,
                    };
                },
            },
        };
    },

    addCommands() {
        return {
            insertInlineMath:
                options =>
                ({ editor, tr }) => {
                    const latex = options.latex;
                    const mathml = setMathMLDisplayAttribute(options.mathml || '', false);

                    const from = options?.pos ?? editor.state.selection.from;

                    if (!latex) {
                        return false;
                    }

                    tr.replaceWith(from, from, this.type.create({ latex, mathml }));
                    return true;
                },

            deleteInlineMath:
                options =>
                ({ editor, tr }) => {
                    const pos = options?.pos ?? editor.state.selection.$from.pos;
                    const node = editor.state.doc.nodeAt(pos);

                    if (!node || node.type.name !== this.name) {
                        return false;
                    }

                    tr.delete(pos, pos + node.nodeSize);
                    return true;
                },

            updateInlineMath:
                options =>
                ({ editor, tr }) => {
                    const latex = options?.latex;
                    const mathml = setMathMLDisplayAttribute(options?.mathml || '', false);
                    let pos = options?.pos;

                    if (pos === undefined) {
                        pos = editor.state.selection.$from.pos;
                    }

                    const node = editor.state.doc.nodeAt(pos);

                    if (!node || node.type.name !== this.name) {
                        return false;
                    }

                    tr.setNodeMarkup(pos, this.type, { ...node.attrs, latex, mathml });

                    return true;
                },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="inline-math"]',
            },
            {
                tag: 'math',
                getAttrs: el => {
                    if (el.getAttribute('display') === 'block') return false;
                    return { latex: '' };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'inline-math' })];
    },

    addInputRules() {
        return [
            new InputRule({
                find: /\\\(([^$\n]+)\\\)$/,
                handler: ({ state, range, match }) => {
                    const [, latex] = match;
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;

                    tr.replaceWith(start, end, this.type.create({ latex }));
                },
            }),
        ];
    },
});

export const InlineMath = InlineMathHeadless.extend({
    addNodeView() {
        const { renderer, katexOptions } = this.options;
        const editor = this.editor;

        return ({ node, getPos }) => {
            const wrapper = document.createElement('span');
            wrapper.className = 'tiptap-mathematics-render';

            wrapper.dataset.type = 'inline-math';
            wrapper.setAttribute('data-latex', node.attrs.latex);

            const isMathJax = !renderer || renderer === 'mathjax' || node.attrs.mathml;

            return createMathematicsNodeView({
                editor,
                node,
                getPos,
                dom: wrapper,
                renderTarget: wrapper,
                isMathJax,
                katexOptions,
                inline: true,
                errorClass: 'inline-math-error',
            });
        };
    },
});
